import React from 'react';
import GenericSpinnerLoader from '../loaders/GenericSpinnerLoader';
import { Tooltip } from 'antd';
import LanguageTranslator from '../locals/LanguageTranslator';
import ReactGA from "react-ga4";
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';
import { sendButtonClickEventToGoogleAnalytics } from '../helper/helperTableFunctions';
import Icons from '../assets/icons';

const FOCUS_STATE_MANUAL = {
  primary: "ring-2 ring-primaryBtnFocus",
  success: "ring-2 ring-successColorFocused",
  default: "ring-2 ring-primaryBtnFocus",
  reject: "ring-2 ring-rejectBtnFocus",
  rejectOutline: "ring-2 ring-rejectBtnFocus border-rejectBtnFocus/10",
  outline: "ring-2 ring-primaryBtnFocus border-primaryBtnFocus",
};

const FOCUS_STATE_SHADOWS = {
  primary: "focus:ring-2 focus:ring-primaryBtnFocus",
  success: "focus:ring-2 focus:ring-successColorFocused",
  default: "focus:ring-2 focus:ring-primaryBtnFocus",
  reject: "focus:ring-2 focus:ring-rejectBtnFocus",
  rejectOutline: "focus:ring-2 focus:ring-rejectBtnFocus focus:border-rejectBtnFocus/10",
  outline: "focus:ring-2 focus:ring-primaryBtnFocus focus:border-primaryBtnFocus",
  tableAction: "focus:bg-primary_w_less_opacity",
  link: 'focus:ring-2 focus:ring-primaryBtnFocus',
  underline: 'focus:text-primary',
  underline2: 'focus:text-fontBlack'
};

const BUTTON_TYPES = {
  primary: "generic-approve-btn",
  success: "generic-success-btn",
  normal: "",
  cancel: "generic-reject-btn",
  default: "generic-default-btn",
  active: "btn-active",
  error: "generic-reject-btn",
  failed: "generic-reject-btn",
  reject: "generic-reject-btn",
  rejectOutline: "bg-white border border-danger text-danger text-xs font-medium hover:border-danger_w_less_opacity disabled:hover:border-[#A9A8A8]",
  outline: 'border border-[#A9A8A8] text-fontBlack text-xs font-medium hover:border-[#111133D1] disabled:hover:border-[#A9A8A8]',
  retry: "p-[8px] rounded-full border border-borderSlateGray",
  tableAction: 'h-[32px] w-[32px] p-[2px] flex items-center justify-center rounded-full hover:bg-primary_w_less_opacity disabled:hover:bg-white',
  link: 'text-primary hover:bg-primaryLight25',
  underline : 'text-xs font-medium flex text-fontBlack items-center gap-1.5 underline hover:text-primary',
  underline2 : 'text-xs font-medium flex items-center gap-1.5 underline text-[#5d5fef] hover:text-fontBlack'
};

const BUTTON_PADDING = {
  small: 'py-0.5 px-2',
  medium : 'py-0.5 px-3',
  large : 'py-0.5 px-3'
}

const getButtonHeight = (size, type) => {
  const baseHeights = {
    large: 'h-[36px]',
    medium: 'h-[32px]',
    small: 'h-[26px]',
  };

  const minWidth = type === 'primary' ? size === 'small' ? 'min-w-[80px]' : 'min-w-[100px]' : '';

  return `${baseHeights[size] || baseHeights.medium} ${minWidth}`;
};

const getButtonClasses = (type, size, className='', isLabelAvailable=false, focused=false) => {
  const isCustomType = type && type !== "normal" && type !== "retry" && type !== "tableAction" && type !== "underline" && type !== "underline2";
  const isButtonTypeValid = BUTTON_TYPES[type] || Object.values(BUTTON_TYPES).includes(className);
  const buttonHeightClass = getButtonHeight(size, type, isLabelAvailable);
  const commonClassName = 'flex items-center justify-center gap-1.5 rounded-[3px] text-xs font-medium';
  const focusClass = focused ? FOCUS_STATE_MANUAL[type] : "";
  
  const baseHeights = {
    small: 'h-[26px] w-[26px] !min-w-[auto]',
    medium: 'h-[32px] w-[32px] !min-w-[auto]',
    large: 'h-[36px] w-[36px] !min-w-[auto]',
  };

  const squareClass = isCustomType && !isLabelAvailable ? baseHeights[size] || baseHeights.medium : '';

  return `
    ${BUTTON_TYPES[type] || ""}
    ${isButtonTypeValid ? `${isCustomType ? buttonHeightClass : ""}` : ""}
    ${isButtonTypeValid && isCustomType ? "rounded-[3px]" : ""}
    ${FOCUS_STATE_SHADOWS[type] || ''}
    ${isCustomType ? commonClassName : ''}
    ${(isCustomType && isLabelAvailable) ? BUTTON_PADDING[size] : squareClass}
    ${focusClass}
    ${className}
  `;
};

const renderIcon = (icon) => (typeof icon === "function" ? icon() : icon);

const GenericCustomButton = ({
  label,
  loading,
  disabled,
  onClick,
  className = '',
  leftIcon,
  rightIcon,
  tooltip = false,
  tooltipClassName,
  tooltipTextClassName,
  tooltipProps = {},
  customTooltip=null,
  type = "normal",
  style = {},
  size = 'medium',
  sendEventAnalyticsToGoogle = false,
  eventAnlyticsLabel = null,
  focused = false,
  btnRef = null,
  rightExtra = null,
  
}) => {
  const location = useLocation();
  let isButtonDisabled = typeof (disabled) == "function" ? disabled() : disabled;
  const isLoading = typeof loading === "function" ? loading() : loading;

  const handleClick = (e) => {
    if (!(isButtonDisabled) && !(typeof (loading) == "function" ? loading() : loading)) {
      // Execute the onClick function only if the button is not disabled or in a loading state
      // console.log("Check label", label);

      if (sendEventAnalyticsToGoogle && (label || eventAnlyticsLabel)) {
        sendButtonClickEventToGoogleAnalytics({
          location: location,
          label: eventAnlyticsLabel ? eventAnlyticsLabel : typeof (label) == "function" ? label() : label,
        })
      }

      onClick && onClick(e);
    }
  };

  const buttonContent = isLoading ? (
    <GenericSpinnerLoader />
  ) : (
    <>
      {type === "retry" && <Icons.ReloadIcon />}
      {renderIcon(leftIcon)}
      <LanguageTranslator>{typeof label === "function" ? label() : label}</LanguageTranslator>
      {renderIcon(rightIcon)}
      {rightExtra ? typeof rightExtra === 'function' ? rightExtra() : rightExtra : null}
    </>
  );

  let button = <button
                  className={getButtonClasses(type, size, className, label ? true : false, focused)}
                  onClick={handleClick}
                  disabled={isButtonDisabled}
                  type='button'
                  data-interactive="true"
                  style={{ position: "relative", ...style }}
                  ref={btnRef}
                >
                  {buttonContent}
                  {customTooltip ? typeof customTooltip === 'function' ?  customTooltip() : customTooltip : null}
                </button>

  return (!customTooltip && tooltip ? 
          <Tooltip overlayStyle={{ fontSize: '12px' }} overlayClassName={`button-tooltip !p-0 w-auto ${tooltipClassName}`}
            title={<LanguageTranslator tag="p" className={`text-center ${tooltipTextClassName}`}>{typeof tooltip === 'function' ? tooltip() : tooltip}</LanguageTranslator>}
            color='black'
            {...tooltipProps}
          >
            {button}
          </Tooltip> : 
          ( button )
  );
};

export default GenericCustomButton;
